import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import axiosInstance from '../../utils/axiosInstance';
import AssetRunCard from '../AssetRunCard';


const ACTIVE_RUN_STATUSES = new Set(['created', 'pending', 'dispatching', 'queued', 'running']);

const label = (value) => String(value || 'unknown')
  .split(/[_\s-]+/)
  .filter(Boolean)
  .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
  .join(' ');

const commandParameters = (command = {}) => (
  Array.isArray(command.parameters) ? command.parameters : []
);

const defaultParameters = (command = {}) => Object.fromEntries(
  commandParameters(command)
    .filter((parameter) => parameter.default !== undefined)
    .map((parameter) => [parameter.key, parameter.default]),
);

const coerceParameter = (value, definition = {}) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const type = String(definition.type || definition.field_type || '').toLowerCase();
  if (type === 'integer') return Number.parseInt(value, 10);
  if (['number', 'float', 'double'].includes(type)) return Number(value);
  if (['boolean', 'bool'].includes(type)) return String(value).toLowerCase() === 'true';
  if (['object', 'array'].includes(type) && typeof value === 'string') return JSON.parse(value);
  return value;
};

function RunSummary({ run, requesterView = false }) {
  if (!run) return null;
  const files = Array.isArray(run.files) ? run.files : [];
  return (
    <Stack spacing={1.25}>
      <AssetRunCard run={run} compact />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button component={RouterLink} to={`/runs/${encodeURIComponent(run.run_id)}`} size="small" variant="outlined">
          View Run Details
        </Button>
        {run.telemetry_context ? (
          <Button component={RouterLink} to={`/runs/${encodeURIComponent(run.run_id)}`} size="small">
            View Telemetry
          </Button>
        ) : null}
      </Stack>
      {files.length ? (
        <Box>
          <Typography variant="caption" color="text.secondary">Run Outputs</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {files.map((file) => (
              <Chip key={file.file_id} size="small" label={file.filename || file.file_name || file.file_id} />
            ))}
          </Stack>
        </Box>
      ) : null}
      {!requesterView && run.command?.runtime_overrides?.length ? (
        <Alert severity="info">
          {run.command.runtime_overrides.length} execution input override{run.command.runtime_overrides.length === 1 ? '' : 's'} recorded for this run.
        </Alert>
      ) : null}
    </Stack>
  );
}

function CandidateDiagnostics({ candidates = [] }) {
  return candidates.length ? (
    <Stack spacing={1}>
      {candidates.map((candidate) => (
        <Paper key={candidate.asset_id} variant="outlined" sx={{ p: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {candidate.asset_name || candidate.asset_id}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            {(candidate.reasons || []).map((reason) => (
              <Chip key={`${candidate.asset_id}:${reason.code}`} size="small" color="warning" label={reason.message || label(reason.code)} />
            ))}
          </Stack>
        </Paper>
      ))}
    </Stack>
  ) : null;
}

export default function CapabilityFulfillmentPanel({ workstream = {}, onRefresh }) {
  const requestCapabilityId = workstream.request_capability_id;
  const mode = workstream.fulfillment_mode || workstream.capability_snapshot?.fulfillment_mode || 'manual';
  const providerView = Boolean(workstream.authorization?.provider);
  const [state, setState] = useState({ loading: mode !== 'manual', error: '', data: null });
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [preview, setPreview] = useState(null);
  const [selectedCommandId, setSelectedCommandId] = useState('');
  const [parameterValues, setParameterValues] = useState({});
  const [artifactIdsText, setArtifactIdsText] = useState('');

  const load = useCallback(async ({ background = false } = {}) => {
    if (!requestCapabilityId || mode === 'manual') return;
    if (!background) setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/fulfillment`,
      );
      setState({ loading: false, error: '', data: response.data });
    } catch (error) {
      setState((current) => ({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load fulfillment state.',
        data: background ? current.data : null,
      }));
    }
  }, [mode, requestCapabilityId]);

  useEffect(() => {
    load();
  }, [load, workstream.status, workstream.updated_at]);

  useEffect(() => {
    const currentRun = state.data?.current_run;
    if (!currentRun || !ACTIVE_RUN_STATUSES.has(currentRun.status)) return undefined;
    const interval = window.setInterval(() => load({ background: true }), 5000);
    return () => window.clearInterval(interval);
  }, [load, state.data?.current_run]);

  const data = state.data || {};
  const currentRun = data.current_run;
  const currentRunActive = Boolean(currentRun && ACTIVE_RUN_STATUSES.has(currentRun.status));
  const attentionItems = Array.isArray(data.attention_items) ? data.attention_items : [];
  const failureDetections = Array.isArray(data.failure_detections) ? data.failure_detections : [];
  const orchestrationNotStarted = failureDetections.some(
    (detection) => detection.code === 'AUTOMATED_ORCHESTRATION_NOT_STARTED',
  );
  const orchestrationSetupFailed = attentionItems.some(
    (attention) => attention.attention_type === 'orchestration_failed',
  );
  const automatedNeedsAction = (
    Boolean(data.intervention_required)
    || data.status === 'awaiting_resource'
    || attentionItems.length > 0
    || failureDetections.length > 0
    || ['failed', 'cancelled', 'failed_to_dispatch', 'abandoned'].includes(currentRun?.status)
  );
  const resourceCandidates = Array.isArray(data.resource_candidates) ? data.resource_candidates : [];
  const recommendedAssets = Array.isArray(data.recommended_assets) ? data.recommended_assets : [];
  const selectableAssets = Array.isArray(data.selectable_assets) ? data.selectable_assets : [];
  const otherSelectableAssets = selectableAssets.filter(
    (asset) => !recommendedAssets.some((recommended) => recommended.asset_id === asset.asset_id),
  );
  const selectedCommand = useMemo(() => (
    (preview?.commands || []).find((command) => command.asset_command_id === selectedCommandId) || null
  ), [preview, selectedCommandId]);

  const refreshAll = async () => {
    await load({ background: true });
    await onRefresh?.();
  };

  const retryAutomated = async () => {
    setBusy('retry');
    setFeedback('');
    try {
      await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/fulfillment/retry`,
        {},
      );
      setFeedback(mode === 'automated'
        ? 'Automated asset selection was retried.'
        : 'Fulfillment setup was retried.');
      await refreshAll();
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to retry fulfillment.';
      await load({ background: true });
      setState((current) => ({ ...current, error: message }));
      await onRefresh?.();
    } finally {
      setBusy('');
    }
  };

  const executeAutomatedAsset = async () => {
    if (!selectedAssetId) return;
    setBusy('select');
    setFeedback('');
    try {
      await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/fulfillment/select-asset`,
        { asset_id: selectedAssetId },
      );
      setFeedback('The selected asset was revalidated and execution was started.');
      await refreshAll();
    } catch (error) {
      const message = error?.response?.data?.message || 'The asset is no longer available.';
      await load({ background: true });
      setState((current) => ({ ...current, error: message }));
      await onRefresh?.();
    } finally {
      setBusy('');
    }
  };

  const loadHybridPreview = async (assetId) => {
    setSelectedAssetId(assetId);
    setPreview(null);
    setFeedback('');
    if (!assetId) return;
    setBusy('preview');
    try {
      const response = await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/fulfillment/preview`,
        { asset_id: assetId },
      );
      const nextPreview = response.data || {};
      const prefill = nextPreview.prefill || {};
      const command = prefill.command || nextPreview.commands?.[0] || null;
      setPreview(nextPreview);
      setSelectedCommandId(prefill.asset_command_id || command?.asset_command_id || '');
      setParameterValues({ ...defaultParameters(command), ...(prefill.parameters || {}) });
      setArtifactIdsText((prefill.artifact_ids || []).join(', '));
    } catch (error) {
      setState((current) => ({ ...current, error: error?.response?.data?.message || 'Unable to prepare this asset.' }));
    } finally {
      setBusy('');
    }
  };

  const chooseCommand = (assetCommandId) => {
    const command = (preview?.commands || []).find((item) => item.asset_command_id === assetCommandId);
    setSelectedCommandId(assetCommandId);
    setParameterValues(defaultParameters(command));
    setArtifactIdsText('');
  };

  const executeHybrid = async () => {
    if (!selectedAssetId || !selectedCommand) return;
    setBusy('execute');
    setFeedback('');
    try {
      const parameters = {};
      commandParameters(selectedCommand).forEach((definition) => {
        const effective = coerceParameter(parameterValues[definition.key], definition);
        if (effective !== undefined) parameters[definition.key] = effective;
      });
      const artifactIds = artifactIdsText.split(',').map((item) => item.trim()).filter(Boolean);
      await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/fulfillment/execute`,
        {
          asset_id: selectedAssetId,
          asset_command_id: selectedCommand.asset_command_id,
          parameters,
          artifact_ids: artifactIds,
        },
      );
      setFeedback('Asset execution was dispatched. Runtime overrides are recorded on the run.');
      await refreshAll();
    } catch (error) {
      setState((current) => ({ ...current, error: error?.response?.data?.message || error?.message || 'Unable to execute this asset.' }));
    } finally {
      setBusy('');
    }
  };

  if (mode === 'manual') return null;
  if (state.loading && !state.data) {
    return <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading fulfillment...</Typography></Stack>;
  }

  if (!providerView) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="overline" color="primary.main">{label(mode)} Fulfillment</Typography>
          <Typography variant="body2" color="text.secondary">
            Fulfillment status: {label(data.status || workstream.status)}
          </Typography>
          {state.error ? <Alert severity="error">{state.error}</Alert> : null}
          {data.intervention_required ? (
            <Alert severity="warning">Fulfillment requires provider attention.</Alert>
          ) : null}
          {currentRun ? <RunSummary run={currentRun} requesterView /> : (
            <Typography variant="body2" color="text.secondary">No asset execution has started yet.</Typography>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
          <Box>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 900 }}>
              {mode === 'automated' ? 'Automated Fulfillment' : 'Hybrid Fulfillment'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mode === 'automated'
                ? 'AMX selects, reserves, dispatches, and monitors the fulfilling asset.'
                : 'Select the asset, review or override its command inputs, then execute.'}
            </Typography>
          </Box>
          <Chip label={label(data.state || data.status || workstream.status)} color={data.status === 'awaiting_resource' ? 'warning' : 'info'} />
        </Stack>
        {state.error ? <Alert severity="error" onClose={() => setState((current) => ({ ...current, error: '' }))}>{state.error}</Alert> : null}
        {feedback ? <Alert severity="success" onClose={() => setFeedback('')}>{feedback}</Alert> : null}
        {currentRun?.command?.status === 'ack_timeout' ? (
          <Alert severity="warning">
            Command delivery was not acknowledged. The asset reservation is retained because delivery is unknown; open Run Details to safely re-issue the same command ID.
          </Alert>
        ) : null}
        {attentionItems.map((attention) => (
          <Alert key={attention.attention_id} severity="error">
            <strong>{attention.title}</strong>: {attention.detail}
          </Alert>
        ))}

        {mode === 'automated' ? (
          <>
            {data.status === 'awaiting_resource' ? (
              <Alert severity="warning">
                AMX could not find an eligible asset. Automatic retries are not enabled.
              </Alert>
            ) : null}
            {failureDetections.map((detection) => (
              <Alert key={detection.code} severity="error">
                <strong>{detection.title}</strong>: {detection.message}
              </Alert>
            ))}
            <CandidateDiagnostics candidates={data.candidate_evaluations || []} />
            {currentRun ? <RunSummary run={currentRun} /> : null}
            {automatedNeedsAction && !currentRunActive ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button startIcon={<RefreshRoundedIcon />} variant="outlined" onClick={retryAutomated} disabled={Boolean(busy)}>
                {busy === 'retry'
                  ? 'Starting...'
                  : orchestrationNotStarted ? 'Start Automated Fulfillment' : 'Retry Fulfillment'}
              </Button>
              <TextField
                select
                size="small"
                label="Select eligible asset"
                value={selectedAssetId}
                onChange={(event) => setSelectedAssetId(event.target.value)}
                sx={{ minWidth: 240 }}
              >
                {resourceCandidates.map((candidate) => (
                  <MenuItem key={candidate.asset_id} value={candidate.asset_id}>
                    {candidate.asset_name || candidate.asset_id}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={executeAutomatedAsset} disabled={!selectedAssetId || Boolean(busy)}>
                {busy === 'select' ? 'Starting...' : 'Select Asset'}
              </Button>
            </Stack>
            ) : null}
          </>
        ) : (
          <>
            {orchestrationSetupFailed ? (
              <Button startIcon={<RefreshRoundedIcon />} variant="outlined" onClick={retryAutomated} disabled={Boolean(busy)}>
                {busy === 'retry' ? 'Retrying...' : 'Retry Fulfillment Setup'}
              </Button>
            ) : null}
            {currentRun ? <RunSummary run={currentRun} /> : null}
            {!currentRunActive ? (
            <>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Recommended</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {recommendedAssets.length ? recommendedAssets.map((candidate) => (
                  <Button key={candidate.asset_id} variant={selectedAssetId === candidate.asset_id ? 'contained' : 'outlined'} onClick={() => loadHybridPreview(candidate.asset_id)} disabled={Boolean(busy)}>
                    {candidate.asset_name || candidate.asset_id}
                  </Button>
                )) : <Typography variant="body2" color="text.secondary">No automation-ready assets.</Typography>}
              </Stack>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Other Attached Assets</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {otherSelectableAssets.length ? otherSelectableAssets.map((candidate) => (
                  <Button key={candidate.asset_id} variant={selectedAssetId === candidate.asset_id ? 'contained' : 'outlined'} onClick={() => loadHybridPreview(candidate.asset_id)} disabled={Boolean(busy)}>
                    {candidate.asset_name || candidate.asset_id} · Manual setup
                  </Button>
                )) : <Typography variant="body2" color="text.secondary">No other selectable assets.</Typography>}
              </Stack>
            </Box>
            {busy === 'preview' ? <CircularProgress size={20} /> : null}
            {preview ? (
              <Stack spacing={1.25}>
                <TextField select size="small" label="Command" value={selectedCommandId} onChange={(event) => chooseCommand(event.target.value)}>
                  {(preview.commands || []).map((command) => (
                    <MenuItem key={command.asset_command_id} value={command.asset_command_id}>
                      {command.display_name || command.operation}
                    </MenuItem>
                  ))}
                </TextField>
                {preview.execution_profile?.is_executable ? (
                  <Alert severity="info">Values were pre-filled from the current execution profile. Changes apply only to this run and are audited as overrides.</Alert>
                ) : (
                  <Alert severity="info">No valid execution profile is available. Choose a command and supply its inputs manually.</Alert>
                )}
                {commandParameters(selectedCommand).map((parameter) => (
                  <TextField
                    key={parameter.key}
                    size="small"
                    label={`${parameter.display_name || parameter.key}${parameter.required ? ' *' : ''}`}
                    value={parameterValues[parameter.key] ?? ''}
                    onChange={(event) => setParameterValues((current) => ({ ...current, [parameter.key]: event.target.value }))}
                    helperText={parameter.description || undefined}
                  />
                ))}
                {selectedCommand?.artifact_inputs?.length || preview.prefill?.artifact_ids?.length ? (
                  <TextField
                    size="small"
                    label="Input Artifact IDs"
                    value={artifactIdsText}
                    onChange={(event) => setArtifactIdsText(event.target.value)}
                    helperText="Comma-separated existing workstream artifact IDs."
                  />
                ) : null}
                <Button startIcon={<PlayArrowRoundedIcon />} variant="contained" onClick={executeHybrid} disabled={!selectedCommand || Boolean(busy)}>
                  {busy === 'execute' ? 'Executing...' : 'Execute Asset'}
                </Button>
              </Stack>
            ) : null}
            </>
            ) : null}
          </>
        )}
      </Stack>
    </Paper>
  );
}
